// Regras de busca + ordenação da tabela "Localidades monitoradas", extraídas
// do LocationsTable para serem compartilhadas com o export CSV (WYSIWYG):
// o arquivo exportado reproduz exatamente a lista que a tabela exibe.
import {
  AgroOverviewLocation,
  LocationsSortKey,
  RiskLevel,
  SortDirection,
} from 'types/Meteorology/Agro/Overview/Dashboard';

// DECISÃO-3: muito_alto é o nível mais severo (última posição no sort asc).
export const RISK_ORDER: Record<RiskLevel, number> = {
  baixo: 0, moderado: 1, alto: 2, muito_alto: 3,
};

export const RISK_LABEL: Record<RiskLevel, string> = {
  baixo: 'Baixo', moderado: 'Moderado', alto: 'Alto', muito_alto: 'Muito alto',
};

// Círculos de "Alerta de Estresse" no mapa (decisão 10/08/2026): mesmas
// cores de texto dos badges de risco da tabela (index.scss do LocationsTable).
// tema: dados-inicio
export const RISK_COLOR: Record<RiskLevel, string> = {
  baixo: '#2a9d8f', moderado: '#b86b00', alto: '#c1121f', muito_alto: '#780000',
};
// tema: dados-fim

export interface LocationColumn {
  key: LocationsSortKey;
  /** Label de exibição da variável — cabeçalho da tabela, modal ⓘ e CSV. */
  label: string;
  /** Override apenas do combobox "Ordenar por" (quando difere do label). */
  sortLabel?: string;
}

/**
 * FONTE ÚNICA das variáveis da tabela "Localidades monitoradas": ordem das
 * colunas e labels consumidos pela tabela, pelo combobox "Ordenar por", pelo
 * modal ⓘ e pelo cabeçalho do CSV. Trocar um label = editar SÓ aqui.
 * (Evolução v2 registrada no contrato: consumir a rota `tabela/variaveis`
 * quando a fonte atualizar os labels oficiais.)
 */
export const LOCATION_COLUMNS: LocationColumn[] = [
  { key: 'diasSemChuva', label: 'Dias s/ chuva', sortLabel: 'N de dias sem chuva' },
  { key: 'armPct', label: 'ARM %' },
  { key: 'chuva7dMm', label: 'Chuva Prevista' },
  { key: 'risco7d', label: 'Risco Hídrico' },
  { key: 'perdaAgua7dMm', label: 'Déficit Hídrico' },
];

/**
 * A fonte pode devolver o nome do local já sufixado com a UF (ex.
 * "Adamantina-SP") enquanto o BE enriquece `uf` em separado (DECISÃO-2) —
 * exibir "nome / UF" duplicaria a sigla. Remove o sufixo "-UF"/"/UF" apenas
 * quando ele corresponde à UF resolvida (espelha a guarda do card 4,
 * DECISÃO-5). "Mogi-Mirim" e afins passam intactos.
 */
export function removerSufixoUf(nome: string, uf: string | null): string {
  if (!uf) return nome;
  const match = nome.match(/^(.*?)\s*[-/]\s*([A-Za-z]{2})$/);
  if (match && match[2].toUpperCase() === uf.toUpperCase()) return match[1];
  return nome;
}

function sortValue(loc: AgroOverviewLocation, key: LocationsSortKey): number | null {
  if (key === 'risco7d') {
    return loc.risco7d === null ? null : RISK_ORDER[loc.risco7d];
  }
  // Déficit Hídrico: pior = mais negativo (decisão 10/08/2026). O valor entra
  // invertido para manter a semântica "desc = pior primeiro" das outras colunas.
  if (key === 'perdaAgua7dMm') {
    return loc.perdaAgua7dMm === null ? null : -loc.perdaAgua7dMm;
  }
  return loc[key];
}

export function sortLocations(
  rows: AgroOverviewLocation[],
  key: LocationsSortKey,
  direction: SortDirection,
): AgroOverviewLocation[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    // DECISÃO-9: null vai para o FIM do sort, em qualquer direção.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * sign;
  });
}

export function filterAndSortLocations(
  rows: AgroOverviewLocation[],
  search: string,
  key: LocationsSortKey,
  direction: SortDirection,
): AgroOverviewLocation[] {
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((l) => `${l.nome} ${l.estado ?? ''}`.toLowerCase().includes(needle))
    : rows;
  return sortLocations(filtered, key, direction);
}
